import { extractErrorMessage } from "../utils/acceptSuggestions";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import UserProfileMenu from "../components/UserProfileMenu";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import RoleBasedInviteModal from "../components/RoleBasedInviteModal";
import BulkInviteCSVModal from "../components/BulkInviteCSVModal";
import RecentPrivilegedActionsWidget from "../components/RecentPrivilegedActionsWidget";
import PendingManualInvitesStrip from "../components/PendingManualInvitesStrip";
import DataSourcesTab from "../components/admin/DataSourcesTab";
import DomainMigrationModal from "../components/admin/DomainMigrationModal";
import {
  Users, Building2, FileText, Shield, AlertTriangle,
  TrendingUp, Download, RefreshCw, ChevronDown, ChevronUp,
  BarChart3, Activity, Calendar, Search, Trash2, UserCog,
  Globe, MapPin, Wifi, Clock, Eye, ArrowRight, ArrowLeft, Zap,
  CheckCircle2, RotateCcw, ClipboardList, Monitor, Plus, UserPlus, Database,
  ShieldOff, UserCheck, UserX, KeyRound, Check, Wrench,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Human-readable labels for every backend role string (mirrors
// services/invite_delivery._ROLE_LABEL_OVERRIDES). Used for the Users-table
// role badge and the role-change dropdown so no machine identifiers surface.
const ROLE_LABELS = {
  admin: "Platform Admin",
  bcm_manager: "BCM Manager",
  department_head: "Department Head",
  it_drp_manager: "IT-DRP Manager",
  process_manager: "Process Manager",
  process_contributor: "Process Contributor",
  app_manager: "Application Manager",
  app_contributor: "Application Contributor",
  it_function_manager: "IT Function Manager",
  it_function_contributor: "IT Function Contributor",
  executive_sponsor: "Executive Sponsor",
  end_user: "End User",
  viewer: "External Auditor / Viewer",
};

// Every valid role the admin may assign (matches UserRole enum in
// backend/models/enums.py). Order is presentation-only.
const ASSIGNABLE_ROLES = [
  "admin",
  "bcm_manager",
  "department_head",
  "it_drp_manager",
  "process_manager",
  "process_contributor",
  "app_manager",
  "app_contributor",
  "it_function_manager",
  "it_function_contributor",
  "executive_sponsor",
  "end_user",
  "viewer",
];

const roleLabel = (r) => ROLE_LABELS[r] || (r ? r.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—");

/* ─── Date helpers for onboarding timestamps ──────────────── */
// "Jul 24, 2026"
function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
}
// "Jul 24, 2026 at 09:31"
function fmtDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${date} at ${time}`;
}
// whole days elapsed since an ISO timestamp
function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}

/* ─── Reusable components ──────────────────────────────── */

function StatCard({ title, value, icon: Icon, color, subtitle }) {
  return (
    <Card className="bg-white border border-slate-200 hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{title}</p>
            <p className={`text-3xl font-bold ${color}`}>{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-xl ${color.replace("text-", "bg-").replace("600", "100").replace("700", "100")}`}>
            <Icon className={`w-6 h-6 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// V22.9.9 — Funnel stage tile (used on Demo Conversion Funnel card)
const FUNNEL_TONES = {
  slate:   { bg: "bg-slate-50",   text: "text-slate-700",   accent: "text-slate-500" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", accent: "text-emerald-600" },
  sky:     { bg: "bg-sky-50",     text: "text-sky-700",     accent: "text-sky-600" },
  violet:  { bg: "bg-violet-50",  text: "text-violet-700",  accent: "text-violet-600" },
};
function FunnelStage({ label, value, sublabel, tone = "slate", testId }) {
  const t = FUNNEL_TONES[tone] || FUNNEL_TONES.slate;
  return (
    <div className={`${t.bg} rounded-lg p-4 border border-slate-100`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-500">{label}</p>
      <p className={`text-3xl font-bold ${t.text} mt-1 leading-none`}>{value}</p>
      {sublabel && <p className={`text-[11px] ${t.accent} mt-1.5 font-medium`}>{sublabel}</p>}
    </div>
  );
}

function EngagementBar({ score }) {
  const color = score >= 75 ? "bg-emerald-500" : score >= 40 ? "bg-amber-500" : "bg-red-400";
  return (
    <div className="flex items-center gap-2 w-28">
      <div className="flex-1 bg-slate-200 rounded-full h-2">
        <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-semibold text-slate-600 w-8 text-right">{score}%</span>
    </div>
  );
}

const STATUS_CONFIG = {
  active: { label: "Active", color: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500" },
  read_only: { label: "Read-Only", color: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
  frozen: { label: "Frozen", color: "bg-red-100 text-red-800", dot: "bg-red-500" },
  converted: { label: "Converted", color: "bg-cyan-100 text-cyan-800", dot: "bg-cyan-500" },
};

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "users", label: "Users", icon: Users },
  { id: "lifecycle", label: "User Lifecycle", icon: ShieldOff, adminOnly: true },
  { id: "organizations", label: "Organizations", icon: Building2 },
  { id: "access-logs", label: "Access Logs", icon: Globe },
  { id: "activity", label: "Activity Feed", icon: Activity },
  { id: "trials", label: "Trials", icon: TrendingUp },
  { id: "analytics", label: "Analytics", icon: Calendar },
  { id: "data-sources", label: "Data Sources", icon: Database },
  { id: "data-repair", label: "Data Repair", icon: Wrench, adminOnly: true },
];

/* ─── Data Repair tab (F24) ───────────────────────────── */

const REPAIR_COLLECTIONS = ["departments", "locations", "process_bia", "risk_assessments",
  "exercises", "recovery_packages", "threat_intelligence", "facilities"];

const repairError = (e) => {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") return { message: d.message, refused: d.refused || [] };
  return { message: d || "Request failed", refused: [] };
};

const IdList = ({ label, ids, highlight = [], testId }) => (
  <div data-testid={testId}>
    <p className="text-xs font-semibold text-slate-600 mb-1">{label} ({ids.length})</p>
    <div className="max-h-40 overflow-y-auto rounded border border-slate-200 bg-slate-50 p-2 font-mono text-xs space-y-0.5">
      {ids.length === 0 ? <span className="text-slate-400">none</span> : ids.map((i) => (
        <div key={i} className={highlight.includes(i) ? "text-amber-700 font-semibold" : "text-slate-700"}>{i}</div>
      ))}
    </div>
  </div>
);

function DataRepairTab({ api }) {
  const [search, setSearch] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [collection, setCollection] = useState("departments");
  const [dry, setDry] = useState(null);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const findOrgs = async () => {
    const res = await api.get(`/admin/organizations?limit=50&search=${encodeURIComponent(search)}`);
    setOrgs(res.data || []);
  };
  const runDry = async () => {
    setBusy(true); setErr(null); setResult(null); setDry(null);
    try {
      const res = await api.get(`/admin/tenant-repair/dry-run?org_id=${encodeURIComponent(orgId)}&collection=${collection}`);
      setDry(res.data);
    } catch (e) { setErr(repairError(e)); } finally { setBusy(false); }
  };
  const apply = async () => {
    setConfirmOpen(false); setBusy(true); setErr(null);
    try {
      const res = await api.post("/admin/tenant-repair/apply", {
        org_id: dry.org_id, collection: dry.collection, ids: dry.master_ids,
        expected_master_count: dry.master_count, expected_own_count: dry.own_count,
      });
      setResult(res.data);
    } catch (e) { setErr(repairError(e)); } finally { setBusy(false); }
  };
  const conflictIds = (dry?.same_name_conflicts || []).flatMap((c) => [...c.master_ids, ...c.own_ids]);

  return (
    <Card data-testid="data-repair-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Wrench className="w-5 h-5" /> Data Repair</CardTitle>
        <CardDescription>Move one organization&apos;s records from the shared store into its own store. Run a dry run first.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Find organization</label>
            <div className="flex gap-2">
              <input data-testid="data-repair-org-search" value={search} onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && findOrgs()} placeholder="Name or domain"
                className="border border-slate-300 rounded-md px-3 py-2 text-sm w-56" />
              <Button variant="outline" data-testid="data-repair-org-search-btn" onClick={findOrgs}><Search className="w-4 h-4" /></Button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Organization</label>
            <select data-testid="data-repair-org-select" value={orgId} onChange={(e) => { setOrgId(e.target.value); setDry(null); setResult(null); }}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm w-72">
              <option value="">Select an organization</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Collection</label>
            <select data-testid="data-repair-collection-select" value={collection} onChange={(e) => { setCollection(e.target.value); setDry(null); setResult(null); }}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm">
              {REPAIR_COLLECTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <Button data-testid="data-repair-dry-run-btn" disabled={!orgId || busy} onClick={runDry}>Dry run</Button>
        </div>

        {err && (
          <div data-testid="data-repair-error" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <p>{err.message}</p>
            {err.refused.length > 0 && (
              <ul className="mt-2 list-disc pl-5 font-mono text-xs">
                {err.refused.map((r) => <li key={r.id}>{r.id}: {r.reason}</li>)}
              </ul>
            )}
          </div>
        )}

        {dry && (
          <div data-testid="data-repair-dry-run-result" className="space-y-4">
            <div className="flex flex-wrap gap-6 text-sm">
              <span>Organization: <strong>{dry.org_name}</strong> ({dry.org_code})</span>
              <span data-testid="data-repair-master-count">Shared store: <strong>{dry.master_count}</strong></span>
              <span data-testid="data-repair-own-count">Own store: <strong>{dry.own_count}</strong></span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <IdList label="In shared store" ids={dry.master_ids} highlight={conflictIds} testId="data-repair-master-ids" />
              <IdList label="In own store" ids={dry.own_ids} highlight={conflictIds} testId="data-repair-own-ids" />
              <IdList label="In both stores" ids={dry.ids_in_both} highlight={dry.ids_in_both} testId="data-repair-both-ids" />
            </div>
            {dry.same_name_conflicts.length > 0 && (
              <div data-testid="data-repair-conflicts" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-semibold mb-1">Same name in both stores ({dry.same_name_conflicts.length})</p>
                {dry.same_name_conflicts.map((c) => <p key={c.name}>{c.name}</p>)}
              </div>
            )}
            <Button data-testid="data-repair-apply-btn" variant="destructive"
              disabled={busy || dry.master_ids.length === 0 || dry.master_ids.length > 500}
              onClick={() => setConfirmOpen(true)}>Apply listed ids</Button>
          </div>
        )}

        {result && (
          <div data-testid="data-repair-apply-result" className={`rounded-md border p-3 text-sm ${result.result === "applied" ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-red-200 bg-red-50 text-red-800"}`}>
            <p>Result: <strong>{result.result}</strong>{result.error ? ` (${result.error})` : ""}. Moved {result.moved}.</p>
            <p>Shared store {result.counts_before.master} → {result.counts_after.master}; own store {result.counts_before.own} → {result.counts_after.own}.</p>
          </div>
        )}

        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent data-testid="data-repair-confirm-dialog">
            <DialogHeader><DialogTitle>Apply data repair?</DialogTitle></DialogHeader>
            {dry && (
              <p className="text-sm text-slate-700">
                Move <strong>{dry.master_ids.length}</strong> record(s) of <strong>{dry.collection}</strong> for <strong>{dry.org_name}</strong> ({dry.org_code}) from the shared store into the organization&apos;s own store.
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" data-testid="data-repair-confirm-cancel" onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button variant="destructive" data-testid="data-repair-confirm-apply" onClick={apply}>Apply</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

/* ─── Main component ───────────────────────────────────── */

export default function AdminDashboardPage() {
  const { user, token, api } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Allow deep-linking from the Onboarding Playbook + sales emails:
  //   /admin?tab=trials  →  Trial Funnel tab
  //   /admin?tab=users   →  Users tab, etc.
  // Falls back to the overview when the param is missing/unknown.
  const VALID_TABS = ["overview", "users", "organizations", "access-logs", "activity", "trials", "analytics", "data-sources", "data-repair"];
  const initialTab = (() => {
    const t = searchParams.get("tab");
    return t && VALID_TABS.includes(t) ? t : "overview";
  })();

  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showBulkInvite, setShowBulkInvite] = useState(false);

  // Overview state
  const [stats, setStats] = useState(null);
  const [activityData, setActivityData] = useState(null);

  // Users state
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Orgs state
  const [organizations, setOrganizations] = useState([]);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [migrationOrg, setMigrationOrg] = useState(null);
  const [orgSearch, setOrgSearch] = useState("");
  // Per-org onboarding progress (WebAdmin) — lazy-loaded on card expand.
  const [orgProgress, setOrgProgress] = useState({});          // org_id -> data | {error:true}
  const [orgProgressLoading, setOrgProgressLoading] = useState(null);
  const [refreshingEnrichmentOrg, setRefreshingEnrichmentOrg] = useState(null);
  const fetchOrgProgress = useCallback(async (orgId) => {
    setOrgProgressLoading(orgId);
    try {
      const res = await api.get(`/admin/organizations/${orgId}/onboarding-progress`);
      setOrgProgress((prev) => ({ ...prev, [orgId]: res.data }));
    } catch (e) {
      setOrgProgress((prev) => ({ ...prev, [orgId]: { error: true } }));
    } finally {
      setOrgProgressLoading(null);
    }
  }, [api]);
  const toggleOrg = (orgId) => {
    const next = expandedOrg === orgId ? null : orgId;
    setExpandedOrg(next);
    if (next && !orgProgress[orgId]) fetchOrgProgress(orgId);
  };

  // WebAdmin-only: force a live re-enrichment (bypasses the 30-day cache).
  const refreshEnrichment = async (org) => {
    setRefreshingEnrichmentOrg(org.id);
    try {
      const res = await api.post("/enrichment/refresh", { org_id: org.id });
      const taskId = res.data?.task_id;
      // The endpoint queues a background waterfall — poll status to completion.
      let finalStatus = null;
      let tries = 0;
      while (taskId && tries < 20) {
        await new Promise((r) => setTimeout(r, 3000));
        tries += 1;
        try {
          const st = await api.get(`/enrichment/status/${org.id}`);
          const status = st.data?.status;
          if (status === "complete" || status === "error") { finalStatus = status; break; }
        } catch { /* keep polling */ }
      }
      await Promise.allSettled([fetchOrganizations(), fetchOrgProgress(org.id)]);
      if (finalStatus === "error") {
        toast.error(`Enrichment refresh failed for ${org.name}`);
      } else if (finalStatus === "complete") {
        toast.success(`Enrichment refreshed successfully for ${org.name}`);
      } else {
        toast.info(`Enrichment for ${org.name} is still processing — check back shortly.`);
      }
    } catch (e) {
      toast.error(`Enrichment refresh failed: ${e.response?.data?.detail || e.message || "unknown error"}`);
    } finally {
      setRefreshingEnrichmentOrg(null);
    }
  };

  // Analytics
  const [registrationData, setRegistrationData] = useState([]);

  // Access Logs state
  const [accessLogs, setAccessLogs] = useState([]);
  const [accessLogStats, setAccessLogStats] = useState(null);
  const [logFilter, setLogFilter] = useState("all");

  // Activity Feed state
  const [activityFeed, setActivityFeed] = useState([]);

  // Trials state
  const [funnel, setFunnel] = useState(null);
  const [trialOrgs, setTrialOrgs] = useState([]);
  const [trialFilter, setTrialFilter] = useState("all");
  const [trialSearch, setTrialSearch] = useState("");
  const [converting, setConverting] = useState(null);

  // V22.9.9 — Demo Conversion Funnel (rolling 14-day)
  const [demoFunnel, setDemoFunnel] = useState(null);

  // Who's Online state
  const [onlineUsers, setOnlineUsers] = useState({ count: 0, users: [] });

  // Provision Tenant state
  const [showProvision, setShowProvision] = useState(false);
  const [provisionForm, setProvisionForm] = useState({ org_name: "", admin_email: "", admin_first_name: "", admin_last_name: "", admin_temp_password: "", industry: "general" });
  const [provisioning, setProvisioning] = useState(false);
  // Industry options sourced from the SAME taxonomy the onboarding wizard uses
  // (GET /api/ai-assistant/industry-taxonomy → INDUSTRY_TAXONOMY), not a duplicate hardcoded list.
  const [industryOptions, setIndustryOptions] = useState([]);
  useEffect(() => {
    api.get("/ai-assistant/industry-taxonomy")
      .then((res) => {
        const tax = res.data?.taxonomy || {};
        setIndustryOptions(
          Object.entries(tax).map(([value, v]) => ({ value, label: v.label || v.name || value }))
        );
      })
      .catch((e) => console.error("industry taxonomy load failed", e));
  }, [api]);

  // User Lifecycle (WebAdmin cross-org account retirement)
  const [lifecycleUsers, setLifecycleUsers] = useState([]);
  const [lifecycleSearch, setLifecycleSearch] = useState("");
  const [lifecycleBusy, setLifecycleBusy] = useState(null);
  const [resendBusy, setResendBusy] = useState(null);
  const [bindingReport, setBindingReport] = useState(null);
  const [bindingLoading, setBindingLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  // Resilient fetch — if a browser extension (Edge Copilot, translator,
  // etc.) wraps fetch and rejects with a clone error, retry once via XHR
  // (which most extensions don't wrap). This keeps the Admin page working
  // inside extension-heavy browsers.
  const safeFetchJson = useCallback(async (url, label) => {
    try {
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`Failed to fetch ${label} (${res.status})`);
      return await res.json();
    } catch (err) {
      const msg = err?.message || "";
      if (!/could not be cloned|postMessage/i.test(msg)) throw err;
      // XHR fallback — bypasses extension fetch-wrappers.
      return await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", url, true);
        xhr.setRequestHeader("Authorization", headers.Authorization);
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch (e) { reject(new Error(`Invalid JSON for ${label}`)); }
          } else {
            reject(new Error(`Failed to fetch ${label} (${xhr.status})`));
          }
        };
        xhr.onerror = () => reject(new Error(`Network error for ${label}`));
        xhr.send();
      });
    }
  }, [token]);

  useEffect(() => {
    if (user?.role !== "admin" && user?.role !== "bcm_manager") {
      navigate("/dashboard");
      return;
    }
    fetchAllData();
  }, [user, navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    // Each fetcher catches its own failure and logs to console so DevTools
    // still surfaces the real problem, but the dashboard always renders with
    // whatever data did come back.
    await Promise.allSettled([
      fetchStats(), fetchUsers(), fetchOrganizations(),
      fetchRegistrationAnalytics(), fetchActivityAnalytics(),
      fetchAccessLogs(), fetchAccessLogStats(),
      fetchActivityFeed(), fetchTrialFunnel(), fetchTrialOrgs(),
      fetchDemoFunnel(),
      fetchWhoOnline(),
      ...(user?.role === "admin" ? [fetchLifecycleUsers()] : []),
    ]);
    setLoading(false);
  };

  /* ── Data fetchers ─────────────────────────────────── */

  // Every fetcher swallows its own failure, logs to console for DevTools
  // visibility, and leaves state as-is. A broken endpoint shows up as an
  // empty panel instead of blocking the whole dashboard.
  const safe = (label, fn) => async () => {
    try {
      await fn();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[AdminDashboard] ${label} failed:`, err?.message || err);
    }
  };

  const fetchStats = safe("stats", async () => {
    setStats(await safeFetchJson(`${API_URL}/api/admin/stats`, "stats"));
  });

  const fetchUsers = safe("users", async () => {
    setUsers(await safeFetchJson(`${API_URL}/api/admin/users?limit=200`, "users"));
  });

  const fetchLifecycleUsers = safe("lifecycle-users", async () => {
    const q = lifecycleSearch.trim() ? `?search=${encodeURIComponent(lifecycleSearch.trim())}` : "";
    setLifecycleUsers(await safeFetchJson(`${API_URL}/api/admin/lifecycle/users${q}`, "lifecycle users"));
  });

  const fetchOrganizations = safe("organizations", async () => {
    const q = orgSearch.trim() ? `&search=${encodeURIComponent(orgSearch.trim())}` : "";
    setOrganizations(await safeFetchJson(`${API_URL}/api/admin/organizations?limit=200${q}`, "organizations"));
  });

  const fetchRegistrationAnalytics = safe("registrations", async () => {
    setRegistrationData(await safeFetchJson(`${API_URL}/api/admin/analytics/registrations?days=30`, "analytics"));
  });

  const fetchActivityAnalytics = safe("activity-analytics", async () => {
    setActivityData(await safeFetchJson(`${API_URL}/api/admin/analytics/activity?days=30`, "activity"));
  });

  const fetchAccessLogs = safe("access-logs", async () => {
    setAccessLogs(await safeFetchJson(`${API_URL}/api/admin/access-logs?limit=200`, "access logs"));
  });

  const fetchAccessLogStats = safe("access-log-stats", async () => {
    setAccessLogStats(await safeFetchJson(`${API_URL}/api/admin/access-logs/stats`, "access log stats"));
  });

  const fetchActivityFeed = safe("activity-feed", async () => {
    setActivityFeed(await safeFetchJson(`${API_URL}/api/admin/activity-feed?limit=100`, "activity feed"));
  });

  const fetchTrialFunnel = async () => {
    try {
      const res = await api.get("/admin/trial/funnel");
      setFunnel(res.data);
    } catch { /* ignore if no trials */ }
  };

  // V22.9.9 — Demo Conversion Funnel (rolling 14-day)
  const fetchDemoFunnel = async () => {
    try {
      const res = await api.get("/demo-requests/funnel");
      setDemoFunnel(res.data);
    } catch { /* ignore if no demo requests yet */ }
  };

  const fetchTrialOrgs = async () => {
    try {
      const res = await api.get("/admin/trial/orgs");
      setTrialOrgs(res.data);
    } catch { /* ignore */ }
  };

  const fetchWhoOnline = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/who-online`, { headers });
      if (res.ok) setOnlineUsers(await res.json());
    } catch { /* ignore */ }
  };

  // Auto-refresh Who's Online every 30 seconds
  useEffect(() => {
    if (loading || !token) return;
    const id = setInterval(fetchWhoOnline, 30000);
    return () => clearInterval(id);
  }, [loading, token]);

  /* ── Provision Tenant ────────────────────────────────── */

  const handleProvision = async () => {
    const { org_name, admin_email, admin_first_name, admin_last_name, admin_temp_password } = provisionForm;
    if (!org_name || !admin_email || !admin_first_name || !admin_last_name || !admin_temp_password) {
      return toast.error("All fields are required");
    }
    if (admin_temp_password.length < 8) {
      return toast.error("Temporary password must be at least 8 characters");
    }
    setProvisioning(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/provision-tenant`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(provisionForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Provisioning failed");
      // Refresh the orgs list AND the stats badges BEFORE we close the dialog so
      // the user always sees the new tenant row before the modal disappears.
      // Awaiting both prevents the stale-list bug reported on the prod deploy.
      await Promise.allSettled([fetchOrganizations(), fetchStats()]);
      toast.success(`Tenant "${org_name}" provisioned successfully!`);
      setShowProvision(false);
      setProvisionForm({ org_name: "", admin_email: "", admin_first_name: "", admin_last_name: "", admin_temp_password: "", industry: "general" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProvisioning(false);
    }
  };

  /* ── Actions ───────────────────────────────────────── */

  const exportUsers = async () => {
    const res = await fetch(`${API_URL}/api/admin/export/users`, { headers });
    if (!res.ok) return toast.error("Export failed");
    const data = await res.json();
    const blob = new Blob([data.csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gbrms_users_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const exportAssessments = async () => {
    const res = await fetch(`${API_URL}/api/admin/export/assessments`, { headers });
    if (!res.ok) return toast.error("Export failed");
    const data = await res.json();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gbrms_assessments_${new Date().toISOString().split("T")[0]}.json`;
    a.click();
  };

  const updateUserRole = async (userId, newRole) => {
    const res = await fetch(`${API_URL}/api/admin/users/${userId}/role?role=${newRole}`, {
      method: "PUT", headers,
    });
    if (res.ok) {
      toast.success(`Role updated to ${roleLabel(newRole)}`);
      fetchUsers();
    } else {
      let msg = "Failed to update role";
      try { const d = await res.json(); if (d?.detail) msg = d.detail; } catch (e) {}
      toast.error(msg);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
      method: "DELETE", headers,
    });
    if (res.ok) fetchUsers();
    else toast.error("Failed to delete user");
  };

  const handleResendCredentials = async (u) => {
    if (!window.confirm(`Send new login credentials to ${u.email}? This will reset their password and they will need to set a new one on next login.`)) return;
    setResendBusy(u.id);
    try {
      const res = await api.post(`/admin/users/${u.id}/resend-credentials`);
      const pw = res.data?.temp_password;
      toast.success(`Credentials sent to ${u.email}.${pw ? ` Temporary password: ${pw}` : ""}`, { duration: 12000 });
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to resend credentials"));
    } finally {
      setResendBusy(null);
    }
  };

  /* ── User Lifecycle actions (WebAdmin) ─────────────── */

  const lifecycleDeactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.email}? They will be blocked from signing in and their password will be reset.`)) return;
    setLifecycleBusy(u.id);
    try {
      await api.post(`/admin/lifecycle/users/${u.id}/deactivate`);
      toast.success(`${u.email} deactivated`);
      fetchLifecycleUsers();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to deactivate user"));
    } finally { setLifecycleBusy(null); }
  };

  const lifecycleReactivate = async (u) => {
    setLifecycleBusy(u.id);
    try {
      await api.post(`/admin/lifecycle/users/${u.id}/reactivate`);
      toast.success(`${u.email} reactivated — ask them to use 'Forgot password'`);
      fetchLifecycleUsers();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to reactivate user"));
    } finally { setLifecycleBusy(null); }
  };

  const lifecycleDelete = async (u) => {
    if (!window.confirm(`Permanently DELETE ${u.email}? This removes the user, their invitations and related notifications. This cannot be undone.`)) return;
    setLifecycleBusy(u.id);
    try {
      const res = await api.delete(`/admin/lifecycle/users/${u.id}`);
      const d = res.data?.deleted;
      toast.success(`${u.email} deleted (${d?.invitations || 0} invite(s), ${d?.invitation_notifications || 0} notification(s) purged)`);
      fetchLifecycleUsers();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to delete user"));
    } finally { setLifecycleBusy(null); }
  };

  const runBindingReport = async () => {
    setBindingLoading(true);
    try {
      const res = await api.get("/admin/lifecycle/org-binding-report");
      setBindingReport(res.data);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to run report"));
    } finally { setBindingLoading(false); }
  };

  const handleConvert = async (orgId, orgName) => {
    if (!window.confirm(`Convert "${orgName}" to paid plan?`)) return;
    setConverting(orgId);
    try {
      await api.post(`/admin/trial/orgs/${orgId}/convert`);
      toast.success(`${orgName} converted to paid plan`);
      fetchTrialFunnel();
      fetchTrialOrgs();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to convert"));
    } finally {
      setConverting(null);
    }
  };

  /* ── Derived data ──────────────────────────────────── */

  const filteredUsers = users.filter(u =>
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.last_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredLogs = logFilter === "all"
    ? accessLogs
    : accessLogs.filter(l => l.login_type === logFilter || (logFilter === "demo" && l.login_type?.startsWith("demo")));

  const filteredTrialOrgs = trialOrgs.filter(o => {
    if (trialFilter !== "all" && o.status !== trialFilter) return false;
    if (trialSearch && !o.name.toLowerCase().includes(trialSearch.toLowerCase()) && !o.code?.toLowerCase().includes(trialSearch.toLowerCase())) return false;
    return true;
  });

  /* ── Render helpers ────────────────────────────────── */

  const timeAgo = (ts) => {
    if (!ts) return "";
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  /* ── Loading / Error ───────────────────────────────── */

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="admin-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-sky-700 mx-auto mb-4" />
          <p className="text-slate-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={fetchAllData} data-testid="admin-retry-btn">Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors mb-1"
              data-testid="admin-back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4" /> BCM Dashboard
            </button>
            <h1 className="text-2xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-sm text-slate-500">Platform management, access monitoring & analytics</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setShowInviteModal(true)}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="admin-invite-bcm-btn"
            >
              <UserPlus className="w-4 h-4" /> Invite BCM Manager
            </Button>
            <Button
              onClick={() => setShowBulkInvite(true)}
              variant="outline"
              className="gap-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
              data-testid="admin-bulk-invite-btn"
            >
              <UserPlus className="w-4 h-4" /> Bulk CSV
            </Button>
            <Button variant="outline" onClick={fetchAllData} className="gap-2" data-testid="admin-refresh-btn">
              <RefreshCw className="w-4 h-4" /> Refresh
            </Button>
            <Button variant="outline" onClick={exportUsers} className="gap-2" data-testid="export-users-btn">
              <Download className="w-4 h-4" /> Export Users
            </Button>
            <Button onClick={exportAssessments} className="gap-2 bg-sky-700 hover:bg-sky-800" data-testid="export-data-btn">
              <Download className="w-4 h-4" /> Export Data
            </Button>
            <div className="pl-2 ml-1 border-l border-slate-200">
              <UserProfileMenu />
            </div>
          </div>
        </div>
      </div>

      <RoleBasedInviteModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvited={() => fetchAllData()}
      />
      <BulkInviteCSVModal
        open={showBulkInvite}
        onClose={() => setShowBulkInvite(false)}
        onInvited={() => fetchAllData()}
      />

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto">
            {TABS.filter(tab => !tab.adminOnly || user?.role === "admin").map(tab => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  // Reflect the active tab in the URL so deep-links + the
                  // browser back button work intuitively.
                  if (tab.id === "overview") {
                    setSearchParams({});
                  } else {
                    setSearchParams({ tab: tab.id });
                  }
                }}
                className={`flex items-center gap-2 py-4 px-3 border-b-2 transition-colors whitespace-nowrap text-sm font-medium ${
                  activeTab === tab.id
                    ? "border-sky-700 text-sky-700"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* ─── Overview Tab ─────────────────────────── */}
        {activeTab === "overview" && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard title="Total Users" value={stats?.total_users ?? "—"} icon={Users} color="text-sky-700" subtitle={`+${stats?.users_this_week ?? 0} this week`} />
              <StatCard title="Organizations" value={stats?.total_organizations ?? "—"} icon={Building2} color="text-emerald-600" />
              <StatCard title="Active Subscriptions" value={stats?.active_subscriptions ?? "—"} icon={Shield} color="text-blue-600" />
              <StatCard title="New Users (30d)" value={stats?.users_this_month ?? "—"} icon={TrendingUp} color="text-purple-600" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <StatCard title="BIA Assessments" value={stats?.total_bia_assessments ?? "—"} icon={FileText} color="text-cyan-600" />
              <StatCard title="Risk Assessments" value={stats?.total_risk_assessments ?? "—"} icon={AlertTriangle} color="text-amber-600" subtitle={`${stats?.high_risk_count ?? 0} high, ${stats?.critical_risk_count ?? 0} critical`} />
              <StatCard title="Plans Generated" value={stats?.total_plans ?? "—"} icon={Activity} color="text-rose-600" />
            </div>

            {/* V22.9.9 — Demo Conversion Funnel (14-day rolling) */}
            {demoFunnel && (
              <Card data-testid="demo-conversion-funnel-card" className="border-violet-100">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Shield className="w-4 h-4 text-violet-600" />
                        Demo Conversion Funnel
                        <Badge className="bg-violet-100 text-violet-700 border-violet-200 text-[10px]">V22.9.9</Badge>
                      </CardTitle>
                      <p className="text-xs text-slate-400 mt-0.5">Rolling 14-day window · public-to-private conversion signal</p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => navigate("/admin/demo-requests")}
                      className="text-xs h-8"
                      data-testid="demo-funnel-open-inbox-btn"
                    >
                      Open inbox <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="demo-funnel-cards">
                    <FunnelStage label="Submissions" value={demoFunnel.submissions ?? 0} sublabel="14-day total" tone="slate" testId="demo-funnel-submissions" />
                    <FunnelStage label="Approvals" value={demoFunnel.approvals ?? 0} sublabel={`${demoFunnel.approval_rate_pct ?? 0}% of submissions`} tone="emerald" testId="demo-funnel-approvals" />
                    <FunnelStage label="First-Login" value={demoFunnel.first_login ?? 0} sublabel={`${demoFunnel.activation_rate_pct ?? 0}% activation`} tone="sky" testId="demo-funnel-first-login" />
                    <FunnelStage label="2nd Session" value={demoFunnel.second_session ?? 0} sublabel={`${demoFunnel.return_rate_pct ?? 0}% return`} tone="violet" testId="demo-funnel-second-session" />
                  </div>
                  {(demoFunnel.deferrals ?? 0) > 0 && (
                    <p className="text-[11px] text-slate-400 mt-3" data-testid="demo-funnel-deferrals">
                      {demoFunnel.deferrals} deferred in the same window (silent re-queue enabled).
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Who's Online */}
            <Card data-testid="who-online-panel">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                    </span>
                    Who's Online
                    <span className="ml-1 text-sm font-normal text-slate-400">({onlineUsers.count} active)</span>
                  </CardTitle>
                  <span className="text-xs text-slate-400">auto-refreshes every 30s</span>
                </div>
              </CardHeader>
              <CardContent>
                {onlineUsers.count === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No users currently active</p>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {onlineUsers.users.map((u) => (
                      <div
                        key={u.id}
                        data-testid={`online-user-${u.id}`}
                        className="flex items-center gap-2.5 bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5 hover:shadow-sm transition-shadow"
                      >
                        <div className="relative">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center text-white text-xs font-bold">
                            {(u.first_name?.[0] || "").toUpperCase()}{(u.last_name?.[0] || "").toUpperCase()}
                          </div>
                          <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{u.first_name} {u.last_name}</p>
                          <p className="text-xs text-slate-400 truncate">{u.org_name} &middot; {u.role}</p>
                        </div>
                        {u.last_active_ip && (
                          <span className="text-[10px] text-slate-400 font-mono ml-1 hidden lg:inline">{u.last_active_ip}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* V22.9 P1 — Recent privileged cross-tenant reads (SOC 2 CC7.2) */}
            <RecentPrivilegedActionsWidget />

            {/* V22.9 P0 — Manual-mode invite hand-delivery queue
                (auto-hides when SendGrid is wired) */}
            <PendingManualInvitesStrip />

            {/* Access snapshot */}
            {accessLogStats && (
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Globe className="w-5 h-5" /> Access Snapshot</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-sky-700">{accessLogStats.total_logins}</p>
                      <p className="text-sm text-slate-500">Total Logins</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-emerald-600">{accessLogStats.logins_24h}</p>
                      <p className="text-sm text-slate-500">Last 24h</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{accessLogStats.logins_7d}</p>
                      <p className="text-sm text-slate-500">Last 7 Days</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-600">{accessLogStats.unique_ips}</p>
                      <p className="text-sm text-slate-500">Unique IPs</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-purple-600">{accessLogStats.demo_logins}</p>
                      <p className="text-sm text-slate-500">Demo Logins</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {activityData && (
              <Card>
                <CardHeader><CardTitle>Activity Summary (Last 30 Days)</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-sky-700">{activityData.bia_assessments}</p>
                      <p className="text-sm text-slate-500">BIA Assessments</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-600">{activityData.risk_assessments}</p>
                      <p className="text-sm text-slate-500">Risk Assessments</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-emerald-600">{activityData.plans_generated}</p>
                      <p className="text-sm text-slate-500">Plans Generated</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-purple-600">{activityData.total_activity}</p>
                      <p className="text-sm text-slate-500">Total Activities</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ─── Users Tab ────────────────────────────── */}
        {activeTab === "users" && (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  data-testid="users-search"
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-700"
                />
              </div>
              <p className="text-sm text-slate-500">{filteredUsers.length} users found</p>
            </div>
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="users-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">User</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">Organization</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">Role</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">Joined</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {filteredUsers.map((u) => (
                        <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                          <td className="px-6 py-4">
                            <p className="font-medium text-slate-900">{u.first_name} {u.last_name}</p>
                            <p className="text-sm text-slate-500">{u.email}</p>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600">{u.org_name}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${u.role === "admin" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-600"}`} data-testid={`user-role-badge-${u.id}`}>
                              {roleLabel(u.role)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${u.subscription_status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                              {u.subscription_status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="text-slate-600 hover:text-sky-700" title="Change role" data-testid={`role-menu-trigger-${u.id}`}>
                                    <UserCog className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto">
                                  <DropdownMenuLabel>Change role</DropdownMenuLabel>
                                  <DropdownMenuSeparator />
                                  {ASSIGNABLE_ROLES.map((r) => (
                                    <DropdownMenuItem
                                      key={r}
                                      disabled={r === u.role}
                                      onSelect={() => { if (r !== u.role) updateUserRole(u.id, r); }}
                                      className="flex items-center justify-between gap-2 cursor-pointer"
                                      data-testid={`role-option-${u.id}-${r}`}
                                    >
                                      <span>{roleLabel(r)}</span>
                                      {r === u.role && <Check className="w-4 h-4 text-sky-600" />}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                              {u.id !== user?.id && u.is_active !== false && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={resendBusy === u.id}
                                  onClick={() => handleResendCredentials(u)}
                                  className="text-slate-600 hover:text-amber-600"
                                  title="Resend login credentials"
                                  data-testid={`resend-credentials-${u.id}`}
                                >
                                  <KeyRound className="w-4 h-4" />
                                </Button>
                              )}
                              {u.id !== user?.id && (
                                <Button variant="ghost" size="sm" onClick={() => deleteUser(u.id)} className="text-slate-600 hover:text-red-600">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ─── User Lifecycle Tab (WebAdmin) ────────────── */}
        {activeTab === "lifecycle" && user?.role === "admin" && (
          <div className="space-y-6" data-testid="lifecycle-tab">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">User Lifecycle</h2>
                <p className="text-sm text-slate-500">Cross-org account retirement — deactivate, reactivate, or delete users. @gbrms.com platform admins are protected.</p>
              </div>
              <Button variant="outline" onClick={runBindingReport} disabled={bindingLoading} className="gap-2" data-testid="binding-report-btn">
                <ClipboardList className="w-4 h-4" />
                {bindingLoading ? "Scanning…" : "Run Org-Binding Report"}
              </Button>
            </div>

            {bindingReport && (
              <Card className={bindingReport.issue_count > 0 ? "border-amber-200" : "border-emerald-200"} data-testid="binding-report-result">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {bindingReport.issue_count > 0
                      ? <AlertTriangle className="w-4 h-4 text-amber-600" />
                      : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                    <p className="text-sm font-medium text-slate-800">
                      {bindingReport.issue_count} mismatch(es) found across {bindingReport.scanned_orgs} organizations
                    </p>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">{bindingReport.note}</p>
                  {bindingReport.issue_count > 0 && (
                    <div className="max-h-56 overflow-y-auto text-xs bg-slate-50 rounded p-2 space-y-1">
                      {bindingReport.issues.map((iss, idx) => (
                        <div key={idx} className="border-b border-slate-100 pb-1" data-testid={`binding-issue-${idx}`}>
                          <span className="font-mono text-amber-700">{iss.type}</span>{" — "}
                          <span className="text-slate-700">{iss.email}</span>{" · "}
                          <span className="text-slate-500">{iss.detail}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  data-testid="lifecycle-search"
                  type="text"
                  placeholder="Search by email or name, then press Enter…"
                  value={lifecycleSearch}
                  onChange={(e) => setLifecycleSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") fetchLifecycleUsers(); }}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-700"
                />
              </div>
              <Button variant="outline" onClick={fetchLifecycleUsers} data-testid="lifecycle-search-btn">Search</Button>
              <p className="text-sm text-slate-500">{lifecycleUsers.length} users</p>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="lifecycle-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">User</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">Organization</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">Role</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {lifecycleUsers.map((u) => {
                        const locked = u.is_self || u.is_platform_admin;
                        return (
                          <tr key={u.id} className="hover:bg-slate-50" data-testid={`lifecycle-row-${u.id}`}>
                            <td className="px-6 py-4">
                              <p className="font-medium text-slate-900">{u.first_name} {u.last_name}</p>
                              <p className="text-sm text-slate-500">{u.email}</p>
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-600">{u.org_name}</td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 text-xs rounded-full ${u.role === "admin" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-600"}`}>{u.role}</span>
                            </td>
                            <td className="px-6 py-4">
                              <span
                                data-testid={`lifecycle-status-${u.id}`}
                                className={`px-2 py-1 text-xs rounded-full ${u.is_active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}
                              >
                                {u.is_active ? "Active" : "Deactivated"}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              {locked ? (
                                <span className="text-xs text-slate-400" data-testid={`lifecycle-locked-${u.id}`}>
                                  {u.is_self ? "Current account" : "Protected admin"}
                                </span>
                              ) : (
                                <div className="flex items-center gap-2">
                                  {u.is_active ? (
                                    <Button
                                      variant="ghost" size="sm" disabled={lifecycleBusy === u.id}
                                      onClick={() => lifecycleDeactivate(u)}
                                      className="text-amber-600 hover:text-amber-700 gap-1"
                                      data-testid={`lifecycle-deactivate-${u.id}`}
                                    >
                                      <ShieldOff className="w-4 h-4" /> Deactivate
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="ghost" size="sm" disabled={lifecycleBusy === u.id}
                                      onClick={() => lifecycleReactivate(u)}
                                      className="text-emerald-600 hover:text-emerald-700 gap-1"
                                      data-testid={`lifecycle-reactivate-${u.id}`}
                                    >
                                      <UserCheck className="w-4 h-4" /> Reactivate
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost" size="sm" disabled={lifecycleBusy === u.id}
                                    onClick={() => lifecycleDelete(u)}
                                    className="text-red-600 hover:text-red-700 gap-1"
                                    data-testid={`lifecycle-delete-${u.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" /> Delete
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}


        {/* ─── Organizations Tab ────────────────────── */}
        {activeTab === "organizations" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 flex-1 max-w-md">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={orgSearch}
                    onChange={(e) => setOrgSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") fetchOrganizations(); }}
                    placeholder="Search organizations by name or domain…"
                    data-testid="org-search-input"
                    className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none"
                  />
                </div>
                <Button onClick={() => fetchOrganizations()} variant="outline" data-testid="org-search-btn">Search</Button>
              </div>
              <Button onClick={() => setShowProvision(true)} data-testid="provision-new-customer-btn" className="bg-sky-600 hover:bg-sky-700 text-white">
                <Plus className="w-4 h-4 mr-2" /> Provision New Customer
              </Button>
            </div>
            <p className="text-xs text-slate-400">Showing {organizations.length} organization(s), newest first.</p>
            {organizations.map((org) => (
              <Card key={org.id} className="overflow-hidden">
                <CardContent className="p-0">
                  <button
                    onClick={() => toggleOrg(org.id)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50"
                    data-testid={`org-row-${org.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-sky-700" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-slate-900">{org.name}</p>
                        <p className="text-sm text-slate-500">{org.user_count} users</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-center"><p className="text-lg font-bold text-cyan-600">{org.bia_count}</p><p className="text-xs text-slate-500">BIA</p></div>
                      <div className="text-center"><p className="text-lg font-bold text-amber-600">{org.risk_count}</p><p className="text-xs text-slate-500">Risks</p></div>
                      <div className="text-center"><p className="text-lg font-bold text-emerald-600">{org.plan_count}</p><p className="text-xs text-slate-500">Plans</p></div>
                      {expandedOrg === org.id ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </button>
                  {expandedOrg === org.id && (
                    <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><p className="text-slate-500">Organization ID</p><p className="font-mono text-slate-700">{org.id}</p></div>
                        <div><p className="text-slate-500">Created</p><p className="text-slate-700">{new Date(org.created_at).toLocaleDateString()}</p></div>
                        <div><p className="text-slate-500">Email Domain</p><p className="text-slate-700">{org.domain ? `@${org.domain}` : "—"}</p></div>
                      </div>
                      <div className="mt-4 flex justify-end gap-2">
                        {user?.role === "admin" && (
                          <Button
                            onClick={() => refreshEnrichment(org)}
                            disabled={refreshingEnrichmentOrg === org.id}
                            variant="outline"
                            className="border-violet-300 text-violet-700 hover:bg-violet-50 disabled:opacity-60"
                            data-testid={`refresh-enrichment-btn-${org.id}`}
                          >
                            <RefreshCw className={`w-4 h-4 mr-2 ${refreshingEnrichmentOrg === org.id ? "animate-spin" : ""}`} />
                            {refreshingEnrichmentOrg === org.id ? "Refreshing enrichment data..." : "Refresh Enrichment"}
                          </Button>
                        )}
                        <Button onClick={() => setMigrationOrg(org)} variant="outline" className="border-sky-300 text-sky-700 hover:bg-sky-50" data-testid={`domain-migration-btn-${org.id}`}>
                          <Globe className="w-4 h-4 mr-2" /> Domain Migration
                        </Button>
                      </div>

                      {/* ── Onboarding Progress (WebAdmin, read-only) ── */}
                      <div className="mt-5 pt-5 border-t border-slate-200" data-testid={`onboarding-progress-${org.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <ClipboardList className="w-4 h-4 text-sky-700" /> Onboarding Progress
                          </p>
                          <button
                            onClick={() => fetchOrgProgress(org.id)}
                            disabled={orgProgressLoading === org.id}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-sky-700 disabled:opacity-50"
                            data-testid={`onboarding-refresh-${org.id}`}
                          >
                            <RefreshCw className={`w-3.5 h-3.5 ${orgProgressLoading === org.id ? "animate-spin" : ""}`} /> Refresh
                          </button>
                        </div>

                        {orgProgressLoading === org.id && !orgProgress[org.id] ? (
                          <p className="text-xs text-slate-400 py-3">Loading progress…</p>
                        ) : orgProgress[org.id]?.error ? (
                          <p className="text-xs text-red-500 py-3">Could not load onboarding progress.</p>
                        ) : orgProgress[org.id] ? (
                          <div className="space-y-4">
                            {/* Provisioned-since indicator + stall-risk warning */}
                            {orgProgress[org.id].created_at && (() => {
                              const days = daysSince(orgProgress[org.id].created_at);
                              const stall = days != null && days > 14 && orgProgress[org.id].overall.percent < 50;
                              return (
                                <div data-testid={`onboarding-provisioned-${org.id}`}>
                                  <p className="text-xs text-slate-500">
                                    Provisioned {days} day{days === 1 ? "" : "s"} ago ({fmtDate(orgProgress[org.id].created_at)})
                                  </p>
                                  {stall && (
                                    <div
                                      className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1"
                                      data-testid={`onboarding-stall-risk-${org.id}`}
                                    >
                                      <AlertTriangle className="w-3.5 h-3.5" /> Stall risk — low progress after 2 weeks
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Overall bar */}
                            <div>
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="font-medium text-slate-600">Overall</span>
                                <span className="font-bold text-slate-800" data-testid={`onboarding-overall-${org.id}`}>
                                  {orgProgress[org.id].overall.completed} of {orgProgress[org.id].overall.total} steps · {orgProgress[org.id].overall.percent}%
                                </span>
                              </div>
                              <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${orgProgress[org.id].overall.percent}%` }} />
                              </div>
                            </div>

                            {/* Activation + Team */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="bg-white border border-slate-200 rounded-lg p-3">
                                <p className="text-xs font-semibold text-slate-500 mb-1.5">Admin Activation</p>
                                {orgProgress[org.id].admin_activation.bcm_manager_email ? (
                                  <div className="space-y-1 text-xs text-slate-700">
                                    <div className="truncate">{orgProgress[org.id].admin_activation.bcm_manager_email}</div>
                                    <div className="flex items-center gap-1.5">
                                      {orgProgress[org.id].admin_activation.password_reset_complete
                                        ? <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Password set</>
                                        : <><Clock className="w-3.5 h-3.5 text-amber-500" /> Password reset pending</>}
                                    </div>
                                    {orgProgress[org.id].admin_activation.password_reset_completed_at && (
                                      <div className="text-slate-500" data-testid={`activation-password-reset-at-${org.id}`}>
                                        Password reset: {fmtDate(orgProgress[org.id].admin_activation.password_reset_completed_at)}
                                      </div>
                                    )}
                                    <div className="text-slate-500" data-testid={`activation-last-login-at-${org.id}`}>
                                      Last login: {orgProgress[org.id].admin_activation.last_login_at
                                        ? fmtDateTime(orgProgress[org.id].admin_activation.last_login_at)
                                        : "never"}
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-xs text-slate-400">No BCM Manager on record</p>
                                )}
                              </div>
                              <div className="bg-white border border-slate-200 rounded-lg p-3">
                                <p className="text-xs font-semibold text-slate-500 mb-1.5">Team</p>
                                <div className="text-xs text-slate-700 space-y-1">
                                  <div>Active users: <span className="font-semibold">{orgProgress[org.id].team.active_users}</span></div>
                                  <div>Invites: <span className="font-semibold">{orgProgress[org.id].team.invites_accepted}</span> accepted · <span className="font-semibold">{orgProgress[org.id].team.invites_pending}</span> pending · {orgProgress[org.id].team.invites_sent} sent</div>
                                </div>
                              </div>
                            </div>

                            {/* Milestone checklist */}
                            <div className="space-y-1.5">
                              {orgProgress[org.id].milestones.map((m) => (
                                <div key={m.key} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid={`milestone-${org.id}-${m.key}`}>
                                  <div className="flex items-center gap-2">
                                    {m.status === "done"
                                      ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                      : m.status === "in_progress"
                                        ? <Clock className="w-4 h-4 text-amber-500" />
                                        : <span className="w-4 h-4 rounded-full border-2 border-slate-300 inline-block" />}
                                    <span className="text-sm text-slate-700">{m.label}</span>
                                  </div>
                                  <div className="flex flex-col items-end">
                                    <span className="text-xs text-slate-400">{m.detail}</span>
                                    {m.completed_at ? (
                                      <span className="text-[11px] text-slate-500" data-testid={`milestone-${org.id}-${m.key}-completed-at`}>
                                        Completed: {fmtDate(m.completed_at)}
                                      </span>
                                    ) : m.started_at ? (
                                      <span className="text-[11px] italic text-slate-400" data-testid={`milestone-${org.id}-${m.key}-started-at`}>
                                        Started: {fmtDate(m.started_at)}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* ─── Access Logs Tab ──────────────────────── */}
        {activeTab === "access-logs" && (
          <div className="space-y-6" data-testid="access-logs-tab">
            {/* Stats row */}
            {accessLogStats && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <StatCard title="Total Logins" value={accessLogStats.total_logins} icon={Eye} color="text-sky-600" />
                <StatCard title="Last 24h" value={accessLogStats.logins_24h} icon={Clock} color="text-emerald-600" />
                <StatCard title="Last 7 Days" value={accessLogStats.logins_7d} icon={Calendar} color="text-blue-600" />
                <StatCard title="Unique IPs" value={accessLogStats.unique_ips} icon={Wifi} color="text-amber-600" />
                <StatCard title="Demo Logins" value={accessLogStats.demo_logins} icon={Monitor} color="text-purple-600" />
                <StatCard title="Standard Logins" value={accessLogStats.standard_logins} icon={Users} color="text-cyan-600" />
              </div>
            )}

            {/* Top countries */}
            {accessLogStats?.top_countries?.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2 text-base"><MapPin className="w-4 h-4" /> Top Countries</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    {accessLogStats.top_countries.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg px-4 py-2">
                        <span className="font-semibold text-slate-800">{c.country}</span>
                        <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full font-bold">{c.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Filter bar */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex gap-1.5">
                {[
                  { key: "all", label: "All" },
                  { key: "standard", label: "Standard" },
                  { key: "demo", label: "Demo" },
                ].map(f => (
                  <button
                    key={f.key}
                    data-testid={`log-filter-${f.key}`}
                    onClick={() => setLogFilter(f.key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                      logFilter === f.key ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <p className="text-sm text-slate-500 ml-auto">{filteredLogs.length} entries</p>
            </div>

            {/* Logs table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="access-logs-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">User</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Organization</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Type</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">IP Address</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Location</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">ISP</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">When</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredLogs.length === 0 ? (
                        <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No access logs recorded yet. Logs will appear after users log in.</td></tr>
                      ) : (
                        filteredLogs.map((log, idx) => (
                          <tr key={log.id || idx} className="hover:bg-slate-50/50" data-testid={`log-row-${idx}`}>
                            <td className="px-4 py-3 font-medium text-slate-800">{log.user_email}</td>
                            <td className="px-4 py-3 text-slate-600">{log.org_name}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 text-xs rounded-full font-bold ${
                                log.login_type === "standard" ? "bg-sky-100 text-sky-700"
                                : log.login_type === "demo" ? "bg-purple-100 text-purple-700"
                                : "bg-amber-100 text-amber-700"
                              }`}>
                                {log.login_type}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-slate-600">{log.ip}</td>
                            <td className="px-4 py-3 text-slate-600">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-slate-400" />
                                {[log.city, log.region, log.country].filter(Boolean).join(", ") || "Unknown"}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-500">{log.isp || "-"}</td>
                            <td className="px-4 py-3 text-xs text-slate-500" title={log.timestamp}>
                              {timeAgo(log.timestamp)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ─── Activity Feed Tab ────────────────────── */}
        {activeTab === "activity" && (
          <div className="space-y-4" data-testid="activity-feed-tab">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Live Activity Feed</h2>
              <p className="text-sm text-slate-500">{activityFeed.length} events</p>
            </div>
            {activityFeed.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-slate-400">
                  <Activity className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p>No activity recorded yet. Events will appear as users interact with the platform.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {activityFeed.map((event, idx) => (
                  <Card key={idx} className="hover:shadow-sm transition-shadow" data-testid={`feed-event-${idx}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          event.type === "login" ? "bg-sky-100" : "bg-amber-100"
                        }`}>
                          {event.type === "login" ? (
                            <Globe className="w-4 h-4 text-sky-600" />
                          ) : (
                            <Activity className="w-4 h-4 text-amber-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-800">{event.description}</p>
                          <div className="flex items-center gap-3 mt-1">
                            {event.ip && (
                              <span className="text-xs text-slate-400 font-mono">{event.ip}</span>
                            )}
                            <span className="text-xs text-slate-400">{timeAgo(event.timestamp)}</span>
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full font-semibold flex-shrink-0 ${
                          event.type === "login" ? "bg-sky-50 text-sky-600" : "bg-amber-50 text-amber-600"
                        }`}>
                          {event.type}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── Trials Tab ───────────────────────────── */}
        {activeTab === "trials" && (
          <div className="space-y-6" data-testid="trials-tab">
            {/* Funnel Cards */}
            {funnel && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="trial-funnel-cards">
                <StatCard title="Total Signups" value={funnel.total_trial_signups} icon={Users} color="text-slate-600" />
                <StatCard title="Active Trials" value={funnel.active_trials} icon={Zap} color="text-emerald-600" subtitle="14 day window" />
                <StatCard title="Read-Only" value={funnel.read_only_grace} icon={Clock} color="text-amber-600" subtitle="Grace period" />
                <StatCard title="Frozen" value={funnel.frozen} icon={AlertTriangle} color="text-red-600" subtitle="Data retained" />
                <StatCard title="Converted" value={funnel.converted_to_paid} icon={CheckCircle2} color="text-cyan-600" />
                <StatCard title="Conv. Rate" value={`${funnel.conversion_rate}%`} icon={TrendingUp} color="text-purple-600" />
                <StatCard title="Expired" value={funnel.expired_trials} icon={AlertTriangle} color="text-slate-600" subtitle="Total expired" />
              </div>
            )}

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  data-testid="trial-search"
                  type="text"
                  placeholder="Search by name or code..."
                  value={trialSearch}
                  onChange={(e) => setTrialSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none"
                />
              </div>
              <div className="flex gap-1.5">
                {[
                  { key: "all", label: "All" },
                  { key: "active", label: "Active" },
                  { key: "read_only", label: "Read-Only" },
                  { key: "frozen", label: "Frozen" },
                  { key: "converted", label: "Converted" },
                ].map(f => (
                  <button
                    key={f.key}
                    data-testid={`trial-filter-${f.key}`}
                    onClick={() => setTrialFilter(f.key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                      trialFilter === f.key ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Trials Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Trial Organizations ({filteredTrialOrgs.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="trial-orgs-table">
                    <thead>
                      <tr className="bg-slate-50 text-left border-b border-slate-200">
                        <th className="px-4 py-3 font-semibold text-slate-600">Organization</th>
                        <th className="px-4 py-3 font-semibold text-slate-600">Status</th>
                        <th className="px-4 py-3 font-semibold text-slate-600">Days</th>
                        <th className="px-4 py-3 font-semibold text-slate-600"><div className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Users</div></th>
                        <th className="px-4 py-3 font-semibold text-slate-600"><div className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> Depts</div></th>
                        <th className="px-4 py-3 font-semibold text-slate-600"><div className="flex items-center gap-1"><ClipboardList className="w-3.5 h-3.5" /> BIA</div></th>
                        <th className="px-4 py-3 font-semibold text-slate-600"><div className="flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" /> Plans</div></th>
                        <th className="px-4 py-3 font-semibold text-slate-600">AI Used</th>
                        <th className="px-4 py-3 font-semibold text-slate-600">Engagement</th>
                        <th className="px-4 py-3 font-semibold text-slate-600">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredTrialOrgs.length === 0 ? (
                        <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">No organizations match the current filter.</td></tr>
                      ) : (
                        filteredTrialOrgs.map((org) => {
                          const sc = STATUS_CONFIG[org.status] || STATUS_CONFIG.active;
                          return (
                            <tr key={org.id} className="hover:bg-slate-50/50 transition" data-testid={`trial-org-row-${org.code}`}>
                              <td className="px-4 py-3">
                                <div className="font-semibold text-slate-800">{org.name}</div>
                                <div className="text-xs text-slate-400">{org.code} &middot; {org.industry}</div>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${sc.color}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                                  {sc.label}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {org.status === "converted" ? (
                                  <span className="text-xs text-cyan-600 font-semibold">Paid</span>
                                ) : (
                                  <div>
                                    <div className="font-semibold text-slate-800">{org.days_remaining}</div>
                                    <div className="text-xs text-slate-400">remaining</div>
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 font-medium text-slate-700">{org.usage?.users}</td>
                              <td className="px-4 py-3 font-medium text-slate-700">{org.usage?.departments}</td>
                              <td className="px-4 py-3 font-medium text-slate-700">{org.usage?.bia_processes}</td>
                              <td className="px-4 py-3 font-medium text-slate-700">{org.usage?.recovery_plans}</td>
                              <td className="px-4 py-3 font-medium text-slate-700">{org.ai_queries_used}</td>
                              <td className="px-4 py-3"><EngagementBar score={org.engagement_score} /></td>
                              <td className="px-4 py-3">
                                {org.status !== "converted" && (
                                  <button
                                    data-testid={`convert-btn-${org.code}`}
                                    onClick={() => handleConvert(org.id, org.name)}
                                    disabled={converting === org.id}
                                    className="inline-flex items-center gap-1 bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-xs px-3 py-1.5 rounded-lg transition disabled:opacity-50"
                                  >
                                    {converting === org.id ? "..." : "Convert"}
                                    <ArrowRight className="w-3 h-3" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ─── Analytics Tab (Registration chart) ───── */}
        {activeTab === "analytics" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5" /> User Registrations (Last 30 Days)</CardTitle>
                <CardDescription>Daily breakdown of new user sign-ups</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-end gap-1">
                  {registrationData.slice(-30).map((day, idx) => {
                    const maxCount = Math.max(...registrationData.map(d => d.count), 1);
                    const height = (day.count / maxCount) * 100;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center group">
                        <div
                          className="w-full bg-sky-700 rounded-t hover:bg-sky-600 transition-colors cursor-pointer"
                          style={{ height: `${Math.max(height, 2)}%` }}
                          title={`${day.date}: ${day.count} registrations`}
                        />
                        {idx % 5 === 0 && (
                          <p className="text-[10px] text-slate-400 mt-1 rotate-45 origin-left">{day.date.slice(5)}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {activityData && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader><CardTitle>Activity Distribution</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[
                        { label: "BIA Assessments", value: activityData.bia_assessments, color: "bg-cyan-500" },
                        { label: "Risk Assessments", value: activityData.risk_assessments, color: "bg-amber-500" },
                        { label: "Plans Generated", value: activityData.plans_generated, color: "bg-emerald-500" },
                      ].map((item) => {
                        const total = activityData.total_activity || 1;
                        const percentage = (item.value / total) * 100;
                        return (
                          <div key={item.label}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-slate-600">{item.label}</span>
                              <span className="font-medium">{item.value}</span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full ${item.color} rounded-full`} style={{ width: `${percentage}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle>Quick Stats</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg text-center">
                        <p className="text-3xl font-bold text-sky-700">{stats?.total_users || 0}</p>
                        <p className="text-sm text-slate-500">Total Users</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg text-center">
                        <p className="text-3xl font-bold text-emerald-600">{stats?.active_subscriptions || 0}</p>
                        <p className="text-sm text-slate-500">Active Subs</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg text-center">
                        <p className="text-3xl font-bold text-amber-600">{stats?.high_risk_count || 0}</p>
                        <p className="text-sm text-slate-500">High Risks</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg text-center">
                        <p className="text-3xl font-bold text-red-600">{stats?.critical_risk_count || 0}</p>
                        <p className="text-sm text-slate-500">Critical Risks</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* ─── Data Sources Tab (Phase 2 · Section 8B) ───── */}
        {activeTab === "data-sources" && <DataSourcesTab />}

        {/* ─── Data Repair Tab (F24) ───── */}
        {activeTab === "data-repair" && user?.role === "admin" && <DataRepairTab api={api} />}
      </div>

      {/* ─── Provision Tenant Dialog ─── */}
      <Dialog open={showProvision} onOpenChange={setShowProvision}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-sky-600" /> Provision New Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Organization Name *</label>
              <input data-testid="provision-org-name" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none" placeholder="e.g., Acme Insurance Ltd" value={provisionForm.org_name} onChange={e => setProvisionForm(p => ({ ...p, org_name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Industry</label>
              <select data-testid="provision-industry" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none" value={provisionForm.industry} onChange={e => setProvisionForm(p => ({ ...p, industry: e.target.value }))}>
                {[{ value: "general", label: "General" }, ...industryOptions].map(i => (
                  <option key={i.value} value={i.value}>{i.label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Admin First Name *</label>
                <input data-testid="provision-first-name" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none" placeholder="Jane" value={provisionForm.admin_first_name} onChange={e => setProvisionForm(p => ({ ...p, admin_first_name: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Admin Last Name *</label>
                <input data-testid="provision-last-name" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none" placeholder="Smith" value={provisionForm.admin_last_name} onChange={e => setProvisionForm(p => ({ ...p, admin_last_name: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Admin Email *</label>
              <input data-testid="provision-email" type="email" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none" placeholder="jane@acme.com" value={provisionForm.admin_email} onChange={e => setProvisionForm(p => ({ ...p, admin_email: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Temporary Password *</label>
              <input data-testid="provision-password" type="text" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-sky-500 focus:outline-none" placeholder="Welcome2026!" value={provisionForm.admin_temp_password} onChange={e => setProvisionForm(p => ({ ...p, admin_temp_password: e.target.value }))} />
              <p className="text-xs text-slate-500 mt-1">Minimum 8 characters. The customer will be forced to change this on first login.</p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowProvision(false)} data-testid="provision-cancel-btn">Cancel</Button>
              <Button onClick={handleProvision} disabled={provisioning} data-testid="provision-submit-btn" className="bg-sky-600 hover:bg-sky-700 text-white">
                {provisioning ? "Provisioning..." : "Provision Tenant"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DomainMigrationModal
        open={!!migrationOrg}
        org={migrationOrg}
        token={token}
        onClose={() => setMigrationOrg(null)}
        onDone={() => { fetchOrganizations(); fetchStats(); }}
      />
    </div>
  );
}
